import { isEmpty } from 'lodash';
import React, { FC, HTMLAttributeAnchorTarget, LegacyRef, useRef } from 'react';
import cls from 'classnames';
import { getProductBaseURL } from '../../../../utils/urls';
import { SmoothStarIcon } from '../../../atoms/Icons';
import Tooltip from '../../../atoms/Tooltip';
import { numberWithThousands } from '../../../../utils/commons';

interface CustomerRatingProps {
  className?: string;
  type?: string;
  slug?: string;
  customersRating: number;
  ratingCount?: number;
  ratingDisplayText?: string;
  shouldUseRatingsLink?: boolean;
  ratingLinkURL?: string;
  direction?: 'horizontal' | 'vertical';
  theme?: 'normal' | 'light';
  wrapLinks?: boolean;
  ratingLinkTarget?: HTMLAttributeAnchorTarget;
  withTooltip?: boolean;
  description?: string;
  subDescription?: string;
}

const CUSTOMER_RATING_TEXT = 'Customer Rating';

const MIN_RATING_COUNT_TO_RATINGS_LIST = 10;

const CustomersRating: FC<CustomerRatingProps> = ({
  className = '',
  customersRating = 0,
  ratingCount = 0,
  ratingDisplayText = '',
  shouldUseRatingsLink = true,
  direction = 'horizontal',
  subDescription,
  wrapLinks = false,
  theme = 'normal',
  ratingLinkTarget = '_blank',
  withTooltip,
  ratingLinkURL,
  description,
  type,
  slug,
}) => {
  const tooltipContainer = useRef<HTMLElement>(null);

  const ratingText = ratingCount > 1 ? 'ratings' : 'rating';
  const boxText = customersRating || 'N/A';
  const descriptionText = ratingDisplayText || CUSTOMER_RATING_TEXT;

  const getLinkText = (): JSX.Element => {
    const baseURL = type ? getProductBaseURL(type) : null;

    const hasRating = ratingCount > 0;
    const rating = numberWithThousands(ratingCount, 5);
    const showRatingsURL = ratingCount >= MIN_RATING_COUNT_TO_RATINGS_LIST && shouldUseRatingsLink && slug && type;
    const ratingsPageURL = showRatingsURL && !isEmpty(baseURL) && !isEmpty(slug) ? `${baseURL}${slug}/reviews/` : '';

    const rText = subDescription || `${rating} ${ratingText}`;

    return (
      <div
        className={cls('customer-rating__link-text-container', {
          'wrap-links': wrapLinks,
        })}
      >
        {hasRating && (
          <>
            {showRatingsURL ? (
              <a
                href={ratingLinkURL || ratingsPageURL}
                className={cls('link', {
                  'has-link': showRatingsURL,
                })}
                rel="noreferrer"
                target={ratingLinkTarget}
              >
                {rText}
              </a>
            ) : (
              <span>{rText}</span>
            )}
          </>
        )}

        {!hasRating ? (
          <a
            href={`/review-mattress/${type}/${slug}`}
            className={cls('link has-link')}
            rel="noreferrer"
            target="_blank"
          >
            Write a review
          </a>
        ) : null}
      </div>
    );
  };

  const getRatingBox = (): JSX.Element => (
    <div className="customer-rating__box">
      {customersRating ? <SmoothStarIcon className="customer-rating__box__star" /> : null}
      {boxText}
    </div>
  );

  const getTooltipContent = (): JSX.Element => (
    <div
      dangerouslySetInnerHTML={{
        __html: !isEmpty(description) ? description! : '',
      }}
    />
  );

  return (
    <div
      className={cls('customer-rating', className, direction, theme, {
        'no-data': !customersRating,
      })}
      ref={tooltipContainer as LegacyRef<HTMLDivElement>}
    >
      {withTooltip && description ? (
        <Tooltip
          id="customer-rating-tooltip"
          container={tooltipContainer.current || undefined}
          content={getTooltipContent()}
        >
          {getRatingBox()}
        </Tooltip>
      ) : (
        getRatingBox()
      )}
      <div className="customer-rating__description">
        {withTooltip ? CUSTOMER_RATING_TEXT : descriptionText}
        {getLinkText()}
      </div>
    </div>
  );
};

export default CustomersRating;
